import React, { useEffect, useState } from 'react';
import {
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import UpIcon from '@material-ui/icons/KeyboardArrowUp';
import { FormattedMessage, useIntl } from 'react-intl';
import { Redirect } from 'react-router-dom';
import SortAsc from '../../assets/images/llantas/table/sort-asc.svg';
import SortDesc from '../../assets/images/llantas/table/sort-desc.svg';
import Sort from '../../assets/images/llantas/table/sort.svg';
import { StyledTableCellProps as StyledTableCell, StyledTooltip } from '../../commons/utils/TableStyles.js';
import * as Utils from '../../commons/utils/Utils.js';
import InventarioItemsGridRow from './InventarioItemsGridRow.jsx';
import InventarioItemsGridFiltros from './InventarioItemsGridFiltros.jsx';
import InventarioItemsImportModal from './InventarioItemsImportModal.jsx';
import Security from '../../commons/security/Security.js';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';

const COLUMN_COUNT = 12;

const PAGINATION_INIT = { page: 0, count: 0, perPage: 10 };

const FILTER_QUERY_INIT = {
  numeroDeParte: '',
  externalCode: '',
  descripcion: '',
  categoria: '',
  subcategoria: '',
  marca: '',
  showInactivos: false,
  soloBajoMinimo: false,
};

const SORT_QUERY_INIT = {
  numeroDeParte: '',
  externalCode: '',
  descripcion: 'asc',
  marca: '',
  modelo: '',
  costo: '',
  stockMinimo: '',
  stockActual: '',
};

const InventarioItemsGrid = (props) => {
  const intl = useIntl();

  const [loading, setLoading] = useState(false);
  const [redirectTo, setRedirectTo] = useState(null);

  const [searchActive, setSearchActive] = useState(false);
  const [pagination, setPagination] = useState({ ...PAGINATION_INIT });
  const [count, setCount] = useState(0);

  const [filterResetInputs, setFilterResetInputs] = useState(false);
  const [filterQuery, setFilterQuery] = useState({ ...FILTER_QUERY_INIT });

  const [sortQuery, setSortQuery] = useState({ ...SORT_QUERY_INIT });
  const [dropdownActive, setDropdownActive] = useState(false);

  const [rows, setRows] = useState([]);

  const [modalImportOpen, setModalImportOpen] = useState(false);

  const [permissions, setPermissions] = useState({
    crear: false,
    visualizar: false,
    modificar: false,
    desactivar: false,
    importar: false,
  });

  useEffect(() => {
    setLoading(true);
    if (Security.hasPermission('INVENTARIO_ITEMS_LISTAR') && ConfigBusiness.get('inventarioItems.habilitado') === 'true') {
      securityPermission();
      fetchRows();
    } else {
      setRedirectTo('/error');
    }
    return () => {};
  }, [pagination, filterQuery, sortQuery]);

  const securityPermission = () => {
    let aux = { ...permissions };
    aux.crear = Security.hasPermission('INVENTARIO_ITEMS_CREAR');
    aux.visualizar = Security.hasPermission('INVENTARIO_ITEMS_VISUALIZAR');
    aux.modificar = Security.hasPermission('INVENTARIO_ITEMS_MODIFICAR');
    aux.desactivar = Security.hasPermission('INVENTARIO_ITEMS_DESACTIVAR');
    aux.importar = Security.hasPermission('INVENTARIO_ITEMS_IMPORTAR');
    setPermissions(aux);
  };

  const fetchRows = () => {
    Utils.getData(`/inventario/items/grid${getQuery()}`)
      .then((data) => {
        setRows((data && data.items) || []);
        setCount((data && data.pagination && data.pagination.count) || 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const getQuery = () => {
    let query = '?page=' + (pagination.page + 1); // +1 porque la API pagina en base 1 y el front en base 0
    query += '&perPage=' + pagination.perPage;
    query += '&numeroDeParte=' + encodeURIComponent(filterQuery.numeroDeParte || '');
    query += '&externalCode=' + encodeURIComponent(filterQuery.externalCode || '');
    query += '&descripcion=' + encodeURIComponent(filterQuery.descripcion || '');
    query += '&categoria=' + (filterQuery.categoria || '');
    query += '&subcategoria=' + (filterQuery.subcategoria || '');
    query += '&marca=' + (filterQuery.marca || '');
    query += '&showInactivos=' + (filterQuery.showInactivos ? 'true' : 'false');
    query += '&soloBajoMinimo=' + (filterQuery.soloBajoMinimo ? 'true' : 'false');
    Object.keys(SORT_QUERY_INIT).forEach((key) => {
      if (sortQuery[key]) query += '&' + key + 'Sort=' + sortQuery[key];
    });
    return query;
  };

  const handleChangePage = (event, newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    setPagination({ ...pagination, perPage: parseInt(event.target.value, 10), page: 0 });
  };

  const requestSort = (key) => {
    let query = { ...sortQuery };
    let aux = sortQuery[key];
    Object.keys(SORT_QUERY_INIT).forEach((k) => {
      query[k] = '';
    });
    if (!aux) query[key] = 'asc';
    else if (aux === 'desc') query[key] = 'asc';
    else query[key] = 'desc';
    setPagination({ ...pagination, page: 0 });
    setSortQuery(query);
  };

  const handleFilterReset = () => {
    setPagination(PAGINATION_INIT);
    setFilterQuery(FILTER_QUERY_INIT);
    setDropdownActive(false);
    setSortQuery(SORT_QUERY_INIT);
    setSearchActive(false);
    setFilterResetInputs(true);
  };

  const handleFilterChange = () => {
    setPagination((prevState) => ({ ...prevState, page: 0 }));
  };

  const sortIcon = (key) => (
    <img
      className="ml-1"
      src={sortQuery[key] === '' ? Sort : sortQuery[key] === 'asc' ? SortAsc : SortDesc}
      alt={'/'}
      style={{ height: '12px', width: '22px' }}
    />
  );

  return (
    <>
      {redirectTo && <Redirect push to={redirectTo} />}
      <div className="row mt-2">
        <div className="col-12">
          <div className="card">
            <div className="card-content collpase show">
              <div className="card-body card-dashboard">
                <div className="container-fluid">
                  <div className="row dt-icons">
                    <div className="col">
                      {permissions.crear && (
                        <StyledTooltip
                          title={intl.formatMessage({ id: 'Nuevo', defaultMessage: 'Nuevo' })}
                          arrow
                          placement="right"
                        >
                          <div
                            className="btn btn-primary box-shadow-2 btn-round btn-lg btn-dt-main round-icon"
                            style={{ height: 55 }}
                            onClick={() => setRedirectTo(props.match.url + '/add')}
                          >
                            <i className="ft-plus"></i>
                          </div>
                        </StyledTooltip>
                      )}
                      {permissions.importar && (
                        <StyledTooltip
                          title={intl.formatMessage({
                            id: 'inventarioItems.import.titulo',
                            defaultMessage: 'Importación masiva de ítems',
                          })}
                          arrow
                          placement="right"
                        >
                          <div
                            className="btn btn-primary box-shadow-2 btn-round btn-lg btn-dt-main round-icon"
                            style={{ height: 55 }}
                            onClick={() => setModalImportOpen(true)}
                          >
                            <i className="ft-upload"></i>
                          </div>
                        </StyledTooltip>
                      )}
                      <div
                        className="btn btn-grey-blue box-shadow-2 btn-round btn-lg btn-dt-main round-icon"
                        style={{ height: 52 }}
                        onClick={() => setDropdownActive(!dropdownActive)}
                      >
                        {dropdownActive ? <UpIcon /> : <ExpandMoreIcon />}
                      </div>
                    </div>
                    <div className="col">
                      {searchActive && (
                        <div className="btn-dt-main round-icon">
                          <div
                            className="text-center"
                            style={{
                              height: '25px',
                              borderRadius: '3px',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              paddingLeft: '8px',
                              backgroundColor: '#5AAC43',
                              color: 'white',
                            }}
                          >
                            <div className="text-center" style={{ width: '100%', fontSize: '12px', fontWeight: '500' }}>
                              {intl.formatMessage(
                                { id: 'search.results', defaultMessage: '{shown} de {total} resultados de búsqueda' },
                                { shown: rows.length, total: count }
                              )}
                            </div>
                            <div className="resultados-busqueda-notif">
                              <i className="las la-times la-xs cursor-pointer" onClick={() => handleFilterReset()}></i>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <InventarioItemsGridFiltros
                  dropdownActive={dropdownActive}
                  setDropdownActive={setDropdownActive}
                  filterResetInputs={filterResetInputs}
                  setFilterResetInputs={setFilterResetInputs}
                  filterQuery={filterQuery}
                  setFilterQuery={setFilterQuery}
                  searchActive={searchActive}
                  setSearchActive={setSearchActive}
                  pagination={pagination}
                  handleFilterChange={handleFilterChange}
                  loading={loading}
                />

                <TableContainer>
                  <Table aria-label="inventario items table" className="table-hover">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell align="left" onClick={() => requestSort('numeroDeParte')} style={{ cursor: 'pointer' }}>
                          <FormattedMessage id="inventarioItems.grid.column_numero_de_parte" defaultMessage="Nº de parte" />
                          {sortIcon('numeroDeParte')}
                        </StyledTableCell>
                        <StyledTableCell align="left" onClick={() => requestSort('externalCode')} style={{ cursor: 'pointer' }}>
                          <FormattedMessage id="inventarioItems.grid.column_gtin" defaultMessage="GTIN" />
                          {sortIcon('externalCode')}
                        </StyledTableCell>
                        <StyledTableCell align="left" onClick={() => requestSort('descripcion')} style={{ cursor: 'pointer' }}>
                          <FormattedMessage id="inventarioItems.grid.column_descripcion" defaultMessage="Descripción" />
                          {sortIcon('descripcion')}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          <FormattedMessage id="inventarioItems.grid.column_categoria" defaultMessage="Categoría" />
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          <FormattedMessage id="inventarioItems.grid.column_subcategoria" defaultMessage="Subcategoría" />
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          <FormattedMessage id="inventarioItems.grid.column_unidad" defaultMessage="Unidad" />
                        </StyledTableCell>
                        <StyledTableCell align="left" onClick={() => requestSort('marca')} style={{ cursor: 'pointer' }}>
                          <FormattedMessage id="inventarioItems.grid.column_marca" defaultMessage="Marca" />
                          {sortIcon('marca')}
                        </StyledTableCell>
                        <StyledTableCell align="left" onClick={() => requestSort('modelo')} style={{ cursor: 'pointer' }}>
                          <FormattedMessage id="inventarioItems.grid.column_modelo" defaultMessage="Modelo" />
                          {sortIcon('modelo')}
                        </StyledTableCell>
                        <StyledTableCell align="right" onClick={() => requestSort('costo')} style={{ cursor: 'pointer' }}>
                          <FormattedMessage id="inventarioItems.grid.column_costo" defaultMessage="Costo" />
                          {sortIcon('costo')}
                        </StyledTableCell>
                        <StyledTableCell align="right" onClick={() => requestSort('stockMinimo')} style={{ cursor: 'pointer' }}>
                          <FormattedMessage id="inventarioItems.grid.column_stock_minimo" defaultMessage="Stock mín." />
                          {sortIcon('stockMinimo')}
                        </StyledTableCell>
                        <StyledTableCell align="right" onClick={() => requestSort('stockActual')} style={{ cursor: 'pointer' }}>
                          <FormattedMessage id="inventarioItems.grid.column_stock_actual" defaultMessage="Stock actual" />
                          {sortIcon('stockActual')}
                        </StyledTableCell>
                        <StyledTableCell align="center" style={{ width: '150px' }}>
                          <FormattedMessage id="inventarioItems.grid.column_acciones" defaultMessage="Acciones" />
                        </StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading && (
                        <TableRow>
                          <StyledTableCell colSpan={COLUMN_COUNT} align="center">
                            <CircularProgress />
                          </StyledTableCell>
                        </TableRow>
                      )}
                      {!loading &&
                        (rows.length > 0 ? (
                          rows.map((row) => (
                            <InventarioItemsGridRow
                              intl={intl}
                              key={'GridRow_' + row.id}
                              baseUrl={props.match.url}
                              row={row}
                              fetchRows={fetchRows}
                              permissions={permissions}
                            />
                          ))
                        ) : (
                          <TableRow>
                            <StyledTableCell colSpan={COLUMN_COUNT} align="center">
                              <FormattedMessage
                                id="inventarioItems.grid.no_data_table"
                                defaultMessage="No se encontraron resultados..."
                              />
                            </StyledTableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  component="div"
                  count={count}
                  rowsPerPage={pagination.perPage}
                  page={pagination.page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage={intl.formatMessage({ id: 'Mostrar', defaultMessage: 'Mostrar' })}
                  labelDisplayedRows={({ from, to, count }) =>
                    intl.formatMessage(
                      { id: 'pagination.displayedRows', defaultMessage: 'Mostrando {from} a {to} de {count}' },
                      { from, to, count }
                    )
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <InventarioItemsImportModal
        open={modalImportOpen}
        onClose={() => setModalImportOpen(false)}
        onImportado={fetchRows}
      />
    </>
  );
};

export default InventarioItemsGrid;
